import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, TrendingUp, TrendingDown, Wallet, ArrowRightLeft } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';
import api from '../../lib/api';

const fmtRs = (v) => {
  const n = Number(v || 0);
  if (n < 0) return `Rs. -${Math.abs(n).toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  return `Rs. ${n.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};
const fmtDate = (d) => { if (!d) return ''; const dt = new Date(d); return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}/${dt.getFullYear()}`; };

const CashForecast = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => {
    const t = localStorage.getItem('token'); const u = localStorage.getItem('user');
    if (!t || !u) { navigate('/login'); return; }
    setUser(JSON.parse(u));
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [days]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/reports/cash-forecast?days=${days}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const d = data || {};
  const projected = d.projected_closing || 0;
  const projColor = projected >= 0 ? 'text-[#1E40AF]' : 'text-[#EF4444]';
  const projBg = projected >= 0 ? 'bg-[#DBEAFE] border-[#BFDBFE]' : 'bg-[#FEF2F2] border-[#FCA5A5]';

  return (
    <div className="flex h-screen bg-[#f0f2f5]" data-testid="cash-forecast-page">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarOpen ? 'md:ml-64' : 'md:ml-0'}`}>
        <Navbar user={user} toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[#1a1a2e] mb-6" data-testid="cash-forecast-title">Cash Flow Forecast</h1>

          {/* Period Toggle */}
          <div className="flex gap-2 mb-6" data-testid="period-toggle">
            {[30, 60, 90].map(d => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-5 py-2 rounded-sm text-sm font-medium transition-colors ${days === d ? 'bg-[#1E40AF] text-white' : 'bg-white border border-[#e5e7eb] text-[#6b7280] hover:border-[#1E40AF]'}`}
                data-testid={`period-${d}`}
              >
                {d} Days
              </button>
            ))}
          </div>

          {loading ? (
            <div className="p-8 text-center text-[#6b7280]">Loading forecast...</div>
          ) : data ? (
            <div className="space-y-6">
              {/* Projected cash card */}
              <div className={`border rounded-sm p-4 sm:p-6 text-center ${projBg}`} data-testid="projected-card">
                <p className="text-sm text-[#6b7280] mb-1">Your projected cash in {days} days</p>
                <p className={`text-3xl sm:text-4xl font-bold ${projColor}`} data-testid="projected-value">{fmtRs(projected)}</p>
              </div>

              {/* Warning */}
              {d.cash_warning && (
                <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] p-4 rounded-sm flex items-start gap-3" data-testid="cash-warning">
                  <AlertTriangle className="w-5 h-5 text-[#F59E0B] mt-0.5 shrink-0" />
                  <p className="text-sm text-[#92400E]">{d.cash_warning}</p>
                </div>
              )}

              {/* Data maturity */}
              {!d.has_enough_data && (
                <div className="bg-[#EFF6FF] border border-[#BFDBFE] p-4 rounded-sm text-sm text-[#1E40AF]" data-testid="data-maturity-notice">
                  Your forecast will become more accurate after 30 days of transactions. Currently showing projection based on entered data only.
                </div>
              )}

              {/* Three cards */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Collections */}
                <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-hidden" data-testid="collections-card">
                  <div className="px-5 py-3 bg-[#EFF6FF] border-b border-[#e5e7eb] flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-[#1E40AF]" />
                    <h3 className="text-sm font-semibold text-[#1E40AF]">Expected Collections</h3>
                  </div>
                  <div className="p-5">
                    <p className="text-xl font-bold text-[#1E40AF] mb-3">{fmtRs(d.expected_collections)}</p>
                    <div className="max-h-40 overflow-y-auto">
                      {(d.collection_items || []).map((item, i) => (
                        <div key={i} className="flex justify-between text-xs py-1.5 border-b border-[#e5e7eb] last:border-0">
                          <div>
                            <span className="text-[#1a1a2e] font-medium">{item.invoice_number}</span>
                            <span className="text-[#9ca3af] ml-2">{item.customer_name}</span>
                          </div>
                          <span className="text-[#1E40AF] font-medium">{fmtRs(item.balance_due)}</span>
                        </div>
                      ))}
                      {(d.collection_items || []).length === 0 && <p className="text-xs text-[#9ca3af]">No outstanding invoices</p>}
                    </div>
                    <p className="text-xs text-[#9ca3af] mt-3">Based on your outstanding invoices</p>
                  </div>
                </div>

                {/* Payments */}
                <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-hidden" data-testid="payments-card">
                  <div className="px-5 py-3 bg-[#FEF2F2] border-b border-[#e5e7eb] flex items-center gap-2">
                    <TrendingDown className="w-4 h-4 text-[#991B1B]" />
                    <h3 className="text-sm font-semibold text-[#991B1B]">Expected Payments</h3>
                  </div>
                  <div className="p-5">
                    <p className="text-xl font-bold text-[#EF4444] mb-3">{fmtRs(d.expected_payments + d.projected_expenses)}</p>
                    <div className="max-h-32 overflow-y-auto">
                      {(d.payment_items || []).map((item, i) => (
                        <div key={i} className="flex justify-between text-xs py-1.5 border-b border-[#e5e7eb] last:border-0">
                          <div>
                            <span className="text-[#1a1a2e] font-medium">{item.po_number}</span>
                            <span className="text-[#9ca3af] ml-2">{item.supplier_name}</span>
                          </div>
                          <span className="text-[#EF4444] font-medium">{fmtRs(item.balance)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-[#e5e7eb] text-xs text-[#6b7280]">
                      <p>Avg monthly expenses: {fmtRs(d.monthly_expenses)}</p>
                      <p>Projected expenses ({days}d): {fmtRs(d.projected_expenses)}</p>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm overflow-hidden" data-testid="summary-card">
                  <div className="px-5 py-3 bg-[#f9fafb] border-b border-[#e5e7eb] flex items-center gap-2">
                    <ArrowRightLeft className="w-4 h-4 text-[#6b7280]" />
                    <h3 className="text-sm font-semibold text-[#1a1a2e]">Projection Summary</h3>
                  </div>
                  <div className="p-5 space-y-3">
                    <div className="flex justify-between text-sm"><span className="text-[#6b7280]">Current cash + bank</span><span className="font-medium text-[#1a1a2e]">{fmtRs(d.current_cash)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-[#6b7280]">+ Expected collections</span><span className="font-medium text-[#1E40AF]">+{fmtRs(d.expected_collections)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-[#6b7280]">- Expected payments</span><span className="font-medium text-[#EF4444]">-{fmtRs(d.expected_payments)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-[#6b7280]">- Projected expenses</span><span className="font-medium text-[#EF4444]">-{fmtRs(d.projected_expenses)}</span></div>
                    <div className="border-t border-[#e5e7eb] pt-3">
                      <div className="flex justify-between">
                        <span className="font-bold text-[#1a1a2e]">Projected closing</span>
                        <span className={`font-bold text-lg ${projColor}`}>{fmtRs(projected)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Chart */}
              {(d.weekly_data || []).length > 0 && (
                <div className="bg-white border border-[#e5e7eb] rounded-2xl shadow-sm p-5" data-testid="forecast-chart">
                  <h3 className="text-sm font-semibold text-[#1a1a2e] mb-4">Weekly Projected Cash Position</h3>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={d.weekly_data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                        <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6b7280' }} />
                        <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} tickFormatter={v => `${(v/1000).toFixed(0)}K`} />
                        <Tooltip formatter={v => [fmtRs(v), 'Projected Cash']} labelStyle={{ color: '#1a1a2e' }} />
                        <ReferenceLine y={50000} stroke="#F59E0B" strokeDasharray="3 3" label={{ value: 'Rs. 50K Warning', position: 'right', fill: '#F59E0B', fontSize: 10 }} />
                        <Bar dataKey="projected_cash" fill="#1E40AF" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </main>
      </div>
    </div>
  );
};

export default CashForecast;
